import React from "react";
import { Controller } from "react-hook-form";

import {
  FormInput,
  FormMoneyInput,
  FormPercentageInput,
  InputDate,
} from "../../../../components/Input";
import { SegmentedControl } from "../../../../components/MobileForm";
import { ResponsiveFormSelect } from "../ResponsiveFormSelect";
import {
  PAYMENT_FREQUENCY_OPTIONS,
  RATE_TYPE_OPTIONS,
  RATE_TYPE_SEGMENTS,
  RESET_FREQUENCY_OPTIONS,
  START_DATE_RULES,
} from "../../constants";
import { LiabilityFormFieldsProps } from "./types";
import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  LabelRow,
  TwoColumnRow,
} from "../../../../components/FormPanel";
import { ResponsiveRow, UnitField, UnitSuffix } from "../styles";

export const LiabilityFormFields: React.FC<LiabilityFormFieldsProps> = ({
  control,
  isSubmitting,
  isVariableRate,
  isCompact,
  liabilityTypes,
}) => {
  const interestRateField = (
    <Field>
      <LabelRow>
        <FieldLabel htmlFor="liability-interest-rate">
          Interest Rate *
        </FieldLabel>
      </LabelRow>
      <FormPercentageInput
        id="liability-interest-rate"
        required
        rules={{ required: "Interest Rate is required!" }}
        name="interestRate"
        control={control}
        placeholder="0.000 %"
        handleClear={true}
        disabled={isSubmitting}
      />
    </Field>
  );

  const resetFrequencyField = (
    <Field>
      <LabelRow>
        <FieldLabel htmlFor="reset-frequency-select">Reset *</FieldLabel>
      </LabelRow>
      <ResponsiveFormSelect
        isCompact={isCompact}
        testId="reset-frequency-select"
        rules={{ required: "Reset Frequency is required!" }}
        name="resetFrequency"
        control={control}
        options={RESET_FREQUENCY_OPTIONS}
        sheetTitle="Select reset frequency"
        disabled={isSubmitting}
      />
    </Field>
  );

  return (
    <>
      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="liability-name">Name *</FieldLabel>
          </LabelRow>
          <FormInput
            id="liability-name"
            required
            rules={{
              required: "Name is required!",
              minLength: {
                message: "Name should have more than 3 letters!",
                value: 3,
              },
            }}
            name="name"
            placeholder="e.g. Home Loan"
            control={control}
            handleClear={true}
            disabled={isSubmitting}
            autoFocus={true}
          />
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="liability-institution">Institution</FieldLabel>
          </LabelRow>
          <FormInput
            id="liability-institution"
            name="institution"
            control={control}
            placeholder="e.g. Institution X"
            handleClear={false}
            disabled={isSubmitting}
          />
          <FieldHelper>The bank or lender holding this debt.</FieldHelper>
        </Field>
      </FormCard>

      <FormCard>
        <ResponsiveRow>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="liability-amount">Lent Amount *</FieldLabel>
            </LabelRow>
            <FormMoneyInput
              id="liability-amount"
              required
              rules={{ required: "Lent Amount is required!" }}
              name="amount"
              placeholder="€ 0.00"
              control={control}
              handleClear={true}
              disabled={isSubmitting}
            />
          </Field>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="liability-type-select">
                Liability Type *
              </FieldLabel>
            </LabelRow>
            <ResponsiveFormSelect
              isCompact={isCompact}
              testId="liability-type-select"
              rules={{ required: "Liability Type is required!" }}
              name="liabilityTypeId"
              control={control}
              options={liabilityTypes}
              sheetTitle="Select liability type"
              disabled={isSubmitting}
            />
          </Field>
        </ResponsiveRow>

        <TwoColumnRow>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="liability-start-date">
                Start Date *
              </FieldLabel>
            </LabelRow>
            <InputDate
              id="liability-start-date"
              rules={START_DATE_RULES}
              name="startDate"
              placeholder="YYYY/MM/DD"
              control={control}
              maxDate={new Date()}
              disabled={isSubmitting}
            />
          </Field>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="liability-term">Term *</FieldLabel>
            </LabelRow>
            <UnitField>
              <FormInput
                id="liability-term"
                required
                rules={{
                  required: "Term is required!",
                  min: { message: "Term must be at least 1 year", value: 1 },
                  max: {
                    message: "Term must be 100 years or less",
                    value: 100,
                  },
                }}
                name="term"
                type="number"
                placeholder="e.g. 40"
                control={control}
                handleClear={false}
                disabled={isSubmitting}
              />
              <UnitSuffix>yrs</UnitSuffix>
            </UnitField>
          </Field>
        </TwoColumnRow>
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel>Rate Type *</FieldLabel>
          </LabelRow>
          <Controller
            name="rateType"
            control={control}
            rules={{ required: "Rate Type is required!" }}
            render={({ field }) => (
              <SegmentedControl
                options={RATE_TYPE_SEGMENTS}
                selectedValue={field.value?.label ?? ""}
                disabled={isSubmitting}
                onChange={(value) => {
                  const option = RATE_TYPE_OPTIONS.find(
                    (opt) => opt.label === value,
                  );
                  field.onChange(option);
                }}
              />
            )}
          />
        </Field>

        <Field>
          {isVariableRate ? (
            <TwoColumnRow>
              {interestRateField}
              {resetFrequencyField}
            </TwoColumnRow>
          ) : (
            interestRateField
          )}
        </Field>
      </FormCard>

      <FormCard>
        <TwoColumnRow>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="payment-frequency-select">
                Payment Frequency
              </FieldLabel>
            </LabelRow>
            <ResponsiveFormSelect
              isCompact={isCompact}
              testId="payment-frequency-select"
              required={false}
              name="paymentFrequency"
              control={control}
              options={PAYMENT_FREQUENCY_OPTIONS}
              sheetTitle="Select payment frequency"
              disabled={isSubmitting}
            />
          </Field>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="liability-payment-due-day">
                Payment Due Day
              </FieldLabel>
            </LabelRow>
            <FormInput
              id="liability-payment-due-day"
              rules={{
                min: {
                  message: "Payment Due Day must be between 1 and 31",
                  value: 1,
                },
                max: {
                  message: "Payment Due Day must be between 1 and 31",
                  value: 31,
                },
              }}
              name="paymentDueDay"
              type="number"
              placeholder="1–31"
              control={control}
              handleClear={false}
              disabled={isSubmitting}
            />
          </Field>
        </TwoColumnRow>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="liability-payment-amount">
              Payment Amount
            </FieldLabel>
          </LabelRow>
          <FormMoneyInput
            id="liability-payment-amount"
            name="paymentAmount"
            placeholder="€ 0.00"
            control={control}
            handleClear={true}
            disabled={isSubmitting}
          />
          <FieldHelper>
            Leave blank to auto-calculate the installment from the rate and
            term.
          </FieldHelper>
        </Field>
      </FormCard>
    </>
  );
};
